import { useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useStockAuditDetail } from "@/hooks/useStockAuditDetail"
import FullPageLoading from "@/components/FullPageLoading"
import { apiFetch } from "@/hooks/apiFetch"

export default function StockAuditDetailPage() {
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [isExporting, setIsExporting] = useState(false)

  const { id } = useParams()
  const queryClient = useQueryClient()

  const { data, isLoading, isError } = useStockAuditDetail(id)

  const audit = data?.data?.audit
  const summary = data?.data?.summary
  const results = data?.data?.results ?? []

  // ✅ MOVE useMemo BEFORE any return
  const filteredResults = useMemo(() => {
    return results.filter((item) => {
      const matchesSearch = item.sku
        .toLowerCase()
        .includes(search.toLowerCase())

      const matchesStatus =
        statusFilter === "all" || item.status === statusFilter

      return matchesSearch && matchesStatus
    })
  }, [search, statusFilter, results])

  const isProcessing = audit?.status === "processing"

  const handleExport = async () => {
    setIsExporting(true)

    try {
      await apiFetch(`stock-audit/${id}/export`, { method: "POST" })
      queryClient.invalidateQueries({ queryKey: ["exports"] })
      toast.success(
        "Stock audit export started — download it from the Exports page once it is ready"
      )
    } catch (error) {
      console.error("Stock audit export failed:", error)
      toast.error(error?.message || "Failed to start stock audit export")
    } finally {
      setIsExporting(false)
    }
  }

  // -------------------------
  // NOW SAFE TO RETURN EARLY
  // -------------------------
  if (isLoading) {
    return <FullPageLoading />
  }

  if (isError || !audit) {
    return <div className="p-6 text-red-500">Failed to load stock audit</div>
  }

  return (
    <div className="p-6 space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Stock Audit #{audit.id}</h1>

          <p className="text-sm text-gray-500 mt-1">
            {audit.status} • {audit.created_at}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleExport}
            disabled={
              audit.status !== "completed" ||
              results.length === 0 ||
              isExporting
            }
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {isExporting ? "Starting export..." : "Export"}
          </button>
          <span
            className={`px-4 py-2 rounded-full text-sm font-medium capitalize
            ${
              audit.status === "completed"
                ? "bg-green-100 text-green-700"
                : audit.status === "processing"
                  ? "bg-yellow-100 text-yellow-700"
                  : "bg-red-100 text-red-700"
            }`}
          >
            {audit.status}
          </span>
        </div>
      </div>

      {/* SUMMARY CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <SummaryCard title="Total Items" value={summary?.total_items ?? 0} />
        <SummaryCard title="Matched" value={summary?.matched ?? 0} />
        <SummaryCard title="Mismatch" value={summary?.mismatched ?? 0} />
        <SummaryCard
          title="Missing In System"
          value={summary?.missing_in_system ?? 0}
        />
        <SummaryCard
          title="Missing In Physical"
          value={summary?.missing_in_physical ?? 0}
        />
      </div>

      {/* FILTERS */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row gap-4 justify-between">
          <input
            type="text"
            placeholder="Search SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 w-full lg:w-80"
          />

          <div className="flex flex-wrap gap-2">
            {[
              "all",
              "matched",
              "mismatch",
              "missing_in_system",
              "missing_in_physical",
            ].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm capitalize
                ${
                  statusFilter === status
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {status.replaceAll("_", " ")}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="table-header-surface">
            <tr>
              <th className="text-left px-4 py-3">SKU</th>
              <th className="text-left px-4 py-3">System Qty</th>
              <th className="text-left px-4 py-3">Physical Qty</th>
              <th className="text-left px-4 py-3">Difference</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>

          <tbody>
            {!isProcessing &&
              filteredResults.map((item) => (
                <tr key={item.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-medium">{item.sku}</td>
                  <td className="px-4 py-3">{item.system_qty}</td>
                  <td className="px-4 py-3">{item.physical_qty}</td>
                  <td
                    className={`px-4 py-3 font-semibold ${
                      item.difference === 0 ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {item.difference}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBar status={item.status} />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {isProcessing ? (
          <div className="p-12 text-center">
            <div className="inline-flex flex-col items-center gap-3">
              <div className="h-10 w-10 rounded-full border-4 border-yellow-400 border-t-transparent animate-spin" />

              <div>
                <p className="font-semibold text-gray-700">
                  Processing Stock Audit
                </p>

                <p className="text-sm text-gray-500 mt-1">
                  Results will appear automatically once processing is
                  completed.
                </p>
              </div>
            </div>
          </div>
        ) : filteredResults.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No results found</div>
        ) : null}
      </div>
    </div>
  )
}

/* --------------------------- */
function SummaryCard({ title, value }) {
  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
      <p className="text-sm text-gray-500">{title}</p>
      <h3 className="text-3xl font-bold mt-2">{value}</h3>
    </div>
  )
}

function StatusBar({ status }) {
  const statusStyles = {
    matched: "bg-green-100 text-green-700",
    mismatch: "bg-yellow-100 text-yellow-700",
    missing_in_system: "bg-red-100 text-red-700",
    missing_in_physical: "bg-blue-100 text-blue-700",
  }

  return (
    <span
      className={`px-3 py-1 rounded-full text-sm capitalize ${
        statusStyles[status] ?? "bg-gray-100 text-gray-700"
      }`}
    >
      {status.replaceAll("_", " ")}
    </span>
  )
}
